import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Check } from "lucide-react";
import { Link } from "wouter";
import { useState } from "react";

interface PortalPreviewProps {
  path: "orthopedic" | "gi" | "other" | null;
  selectedRegions: string[];
  onNewSession: () => void;
}

export function PortalPreview({ path, selectedRegions, onNewSession }: PortalPreviewProps) {
  const [symptomLevel, setSymptomLevel] = useState([5]);

  const primaryConcern =
    path === "orthopedic"
      ? selectedRegions.length > 0
        ? selectedRegions[0].replace(/-/g, " ")
        : "orthopedic recovery"
      : path === "gi"
      ? "digestive health"
      : "recovery";

  return (
    <div className="space-y-6">
      <div className="bg-teal-700 text-white px-4 py-2 rounded-lg text-center font-medium">
        CONCEPT PREVIEW
      </div>

      <Card
        className="border-teal-200 dark:border-teal-800 max-w-2xl mx-auto"
        data-testid="portal-preview-card"
      >
        <CardHeader className="border-b border-border pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-700 flex items-center justify-center text-white font-semibold text-sm">
                Rx
              </div>
              <CardTitle className="text-xl font-serif">AgelessRx Patient Portal</CardTitle>
            </div>
            <Badge variant="secondary">Active</Badge>
          </div>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          <div className="p-4 bg-teal-50 dark:bg-teal-950/30 rounded-lg border border-teal-200 dark:border-teal-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-teal-900 dark:text-teal-100">
                BPC-157 — Week 1 of 8
              </h3>
              <span className="text-sm text-teal-700 dark:text-teal-300">12.5% complete</span>
            </div>
            <div className="h-2 bg-teal-200 dark:bg-teal-900 rounded-full overflow-hidden mb-4">
              <div className="h-full bg-teal-700" style={{ width: "12.5%" }} />
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs mb-1">Next Check-In</p>
                <p className="font-medium">August 3, 2025</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Next Refill</p>
                <p className="font-medium">August 17, 2025</p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-card border border-border rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Symptom Check-In</h4>
              <Badge variant="outline" className="text-xs">Weekly</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              How is your {primaryConcern} today?
            </p>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Current level</span>
                <span className="text-lg font-semibold text-teal-700">{symptomLevel[0]}</span>
              </div>
              <Slider
                min={0}
                max={10}
                step={1}
                value={symptomLevel}
                onValueChange={setSymptomLevel}
                className="[&_.bg-primary]:bg-teal-700"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>No symptoms</span>
                <span>Severe</span>
              </div>
            </div>

            <Button className="w-full bg-teal-700 hover:bg-teal-800 text-white border-teal-800">
              Submit Check-In
            </Button>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="w-4 h-4 text-green-600" />
            <span>Refill shipment on track</span>
          </div>

          <Button variant="outline" className="w-full" disabled>
            View Treatment Plan
          </Button>
        </CardContent>
      </Card>

      <div className="max-w-2xl mx-auto space-y-4">
        <p className="text-sm text-muted-foreground text-center leading-relaxed">
          This is a conceptual preview of how the AgelessRx patient portal could connect to your
          intake. No real data is stored or transmitted.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link href="/funnels">
            <Button variant="outline" data-testid="button-return-to-funnels">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Return to intake
            </Button>
          </Link>
          <Button
            onClick={onNewSession}
            variant="outline"
            data-testid="button-new-session"
          >
            Start a new session
          </Button>
        </div>
      </div>
    </div>
  );
}
